<template>
  <div>
    <q-card>
      <q-card-section class="row">
        <div class="text-h6">
          {{ $t('clients.modals.inviteTrainer.textInviteTrainer') }}
        </div>
        <q-space />
        <q-btn
          v-close-popup
          flat
          round
          dense
          icon="close"
        />
      </q-card-section>
      <q-card-section class="q-pt-none">
        <div class="text-caption">
          {{ $t('clients.modals.inviteTrainer.textDescription') }}
        </div>
      </q-card-section>
      <q-form @submit="onSubmitForm">
        <q-card-section class="q-pt-none">
          <q-form class="row q-col-gutter-md">
            <q-input
              v-model="editInvitation.client.clientName"
              class="col-12"
              readonly
              :label="$t('clients.modals.inviteTrainer.inputClientNameLabel')"
            />

            <q-input
              ref="email"
              v-model="editInvitation.email"
              class="col-12"
              outlined
              :label="$t('clients.modals.inviteTrainer.inputTrainerEmailLabel')"
              type="email"
              :rules="rules.email"
              lazy-rules
              hide-bottom-space
            />

            <q-select
              ref="role"
              v-model="editInvitation.role"
              class="col-12"
              outlined
              :options="roles"
              :label="$t('clients.modals.inviteTrainer.inputTrainerRoleLabel')"
              emit-value
              map-options
              hide-bottom-space
              :rules="rules.role"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                >
                  <q-item-section>
                    <!--eslint-disable-next-line vue/no-v-text-v-html-on-component-->
                    <q-item-label v-html="scope.opt.label" />
                    <q-item-label caption>
                      {{ scope.opt.hint }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <div
              v-if="allowMFA"
              class="col-12 q-col-gutter-y-md"
            >
              <div class="row q-col-gutter-md">
                <universal-tooltip
                  show-tooltip
                  :text="$t('users.modals.editUser.inputRequireMFATooltip')"
                >
                  <q-toggle
                    v-if="allowMFA"
                    :model-value="requireMFA"
                    class="q-pl-xs"
                    :label="$t('users.modals.editUser.inputRequireMFALabel')"
                    dense
                    @update:model-value="onToggleRequireMfa"
                  />
                </universal-tooltip>
              </div>

              <div>
                <universal-tooltip
                  :text="$t('users.modals.editUser.requireLoginAndSignInMethodTooltip')"
                  :delay-time="5000"
                  self="center right"
                  show-tooltip
                  max-width="350px"
                >
                  <q-toggle
                    v-model="requireLoginMethod"
                    class="q-pl-xs"
                    :label="$t('users.modals.editUser.requireSignUpMethodLabel')"
                    dense
                  />
                </universal-tooltip>

                <login-method-selector
                  v-if="requireLoginMethod"
                  v-model="loginMethod"
                  class="q-ml-xs q-mt-sm"
                  dense
                  show-icon
                  :disabled-methods="disabledProviders"
                  :disabled-methods-tooltip="$t('users.modals.editUser.tooltipDisableMFAToUseLoginMethod')"
                />
              </div>
            </div>
          </q-form>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn
            v-close-popup
            flat
            :label="$t('common.buttonCancel')"
            color="secondary"
          />
          <q-btn
            flat
            :label="$t('clients.modals.inviteTrainer.buttonSendInvitation')"
            :loading="isSavingInvitation"
            type="submit"
            color="primary"
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </div>
</template>
<script>
import LoginMethodSelector from 'components/UI/LoginMethodSelector.vue';
import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import { every } from 'lodash';
import { mapState } from 'pinia';
import { SSO_PROVIDERS } from 'src/constants/authorization';
import UserInvitationsDB from 'src/fb/userInvitations-db';
import Auth from 'src/mixins/Auth';
import { auth } from 'src/store/auth';
import { isValidEmail, userRoles } from 'src/utils/AppUtils';

export default {
  name: 'InviteTrainer',
  components: { UniversalTooltip, LoginMethodSelector },

  mixins: [Auth],

  props: {
    invitation: {
      type: Object,
      required: true,
    },
  },

  emits: ['send-invitation'],

  data() {
    const roles = [
      {
        label: this.$t('clients.modals.inviteTrainer.roleParent'),
        value: userRoles.PARENT,
      },
      {
        label: this.$t('clients.modals.inviteTrainer.roleTrainer'),
        value: userRoles.TRAINER,
        hint: this.$t('common.userRoleHints.trainer'),
      },
      {
        label: this.$t('clients.modals.inviteTrainer.roleTherapist'),
        value: userRoles.SENIOR_THERAPIST,
        hint: this.$t('common.userRoleHints.senior_therapist'),
      },
    ];

    return {
      editInvitation: {
        email: '',
        role: '',
      },

      requireMFA: false,
      loginMethod: null,
      requireLoginMethod: false,
      isSavingInvitation: false,

      roles,

      rules: {
        email: [
          (val) => isValidEmail(val) || this.$t('clients.modals.inviteTrainer.inputTrainerEmailValidator'),
        ],
        role: [
          (val) => roles.map(({ value }) => value).includes(val)
            || this.$t('clients.modals.inviteTrainer.inputTrainerRoleValidator'),
        ],
      },
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'connectedClinic', 'isSubscriptionPremium']),

    enabledMicrosoft() {
      const clinicsWithMicrosoft = [
        'zB969By46ZJzdfFciamW', // eu
        'YapUgbgJYqbKU9BNzoD0', // us
      ];

      return process.env.ENV_TYPE !== 'production' || clinicsWithMicrosoft.includes(this.userDetails.clinic);
    }, // enabledMicrosoft()

    allowMFA() {
      return this.isSubscriptionPremium;
    }, // allowMFA()

    disabledProviders() {
      return this.requireMFA && SSO_PROVIDERS;
    },

  },

  watch: {
    invitation: {
      immediate: true,
      deep: true,
      handler(newInvitation) {
        this.editInvitation = { ...newInvitation };
      },
    },
  },

  methods: {
    async validateForm() {
      const validateInfo = [
        this.$refs.email && this.$refs.email.validate(),
        this.$refs.role && this.$refs.role.validate(),
      ];
      return every(await Promise.all(validateInfo));
    }, // validateForm()

    onToggleRequireMfa(value) {
      this.requireMFA = value;

      if (SSO_PROVIDERS.includes(this.loginMethod)) {
        this.loginMethod = null;
      }
    },

    async onSubmitForm() {
      if (!await this.validateForm()) {
        return;
      }

      const invitation = {
        clinic: this.userDetails.clinic,
        email: this.editInvitation.email.toLowerCase().trim(),
        invitedByUser: this.userDetails.userId,
        role: this.editInvitation.role,
        userPrivilege: this.editInvitation.userPrivilege || null,
        invitePage: 'signup',
        requireMFA: this.requireMFA,
        requireLoginMethod: this.requireLoginMethod || false,
        loginMethod: this.loginMethod || null,
        units: this.userDetails.units || [],
      };

      // client: this.invitation.client.clientId,
      const userInvitationsDb = new UserInvitationsDB();

      try {
        this.isSavingInvitation = true;
        await userInvitationsDb.create(invitation, invitation.email, true);
        await userInvitationsDb.addClients(invitation.email, [this.invitation.client.clientId]);
        this.$emit('send-invitation', invitation, invitation.invitePage);
      } catch (err) {
        this.$t('clients.modals.inviteTrainer.invitationFailed', { message: err.message });
        return;
      } finally {
        this.isSavingInvitation = false;
      }
    },
  },

};
</script>
